import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * Posity: per-slide timing. Each slide may carry `data-duration` (seconds);
   * slides without it fall back to the section autoplay speed. This lets the
   * Zomerdeals/countdown slide linger (e.g. 15s) while the USP slides cycle
   * faster (~5s). Uses a recursive setTimeout instead of a fixed setInterval.
   */
  play() {
    if (!this.autoplay) return;

    this.paused = false;
    this.#scheduleNext();
  }

  /**
   * Schedules the advance to the next slide using the CURRENT slide's duration.
   */
  #scheduleNext() {
    this.#interval = setTimeout(() => {
      // Don't advance while hovered or tab hidden — just re-check shortly.
      if (this.matches(':hover') || document.hidden) {
        this.#scheduleNext();
        return;
      }

      this.next();
      this.#scheduleNext();
    }, this.#currentSlideDuration());
  }

  /**
   * Resolves the display duration (ms) of the currently visible slide.
   * @returns {number}
   */
  #currentSlideDuration() {
    const slides = this.refs.slides ?? [];
    if (slides.length) {
      let index = this.current % slides.length;
      if (index < 0) index += slides.length;

      const seconds = parseInt(`${slides[index]?.getAttribute('data-duration')}`, 10);
      if (!Number.isNaN(seconds)) return seconds * 1000;
    }

    return this.autoplayInterval;
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearTimeout(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   * Posity: bij terugkeren moet `paused` eerst gewist worden, anders keert `resume()`
   * meteen terug (die bewaakt op `this.paused`) en staat de bar na één tabwissel
   * permanent stil tot een reload.
   */
  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.pause();
      return;
    }

    this.paused = false;
    this.resume();
  };
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
